<script setup lang="ts">
import { ref, watch, onMounted, nextTick } from 'vue'
import { useSortable } from '@vueuse/integrations/useSortable'
import { VButton, VIcon, VAvatar } from '@/modules/shared/components'
import { useTeamMemberStore } from '../stores/team-member-store'
import TeamMemberForm from './team-member-form.vue'
import type { TeamMember } from '../types'

const props = defineProps<{
  candidateId: string
  disabled?: boolean
}>()

const teamMemberStore = useTeamMemberStore()
const formRef = ref<InstanceType<typeof TeamMemberForm> | null>(null)
const editingMember = ref<TeamMember | null>(null)
const sortableRef = ref<HTMLElement | null>(null)
const localMembers = ref<TeamMember[]>([])

const setupSortable = () => {
  if (!sortableRef.value) return

  useSortable(sortableRef, localMembers, {
    handle: '.drag-handle',
    animation: 150,
    onEnd: async (evt) => {
      const { oldIndex, newIndex } = evt
      if (oldIndex !== undefined && newIndex !== undefined && oldIndex !== newIndex) {
        // Manually reorder the array since useSortable doesn't mutate it
        const item = localMembers.value.splice(oldIndex, 1)[0]
        localMembers.value.splice(newIndex, 0, item)

        const memberIds = localMembers.value.map((m) => m.id)
        await teamMemberStore.reorderTeamMembers(props.candidateId, memberIds)
      }
    },
  })
}

onMounted(async () => {
  if (props.candidateId) {
    await teamMemberStore.fetchTeamMembers(props.candidateId)
    localMembers.value = [...teamMemberStore.members]
    await nextTick()
    setupSortable()
  }
})

watch(
  () => props.candidateId,
  async (newId) => {
    if (newId) {
      teamMemberStore.reset()
      await teamMemberStore.fetchTeamMembers(newId)
      localMembers.value = [...teamMemberStore.members]
      await nextTick()
      setupSortable()
    }
  },
)

const syncLocalMembers = () => {
  localMembers.value = [...teamMemberStore.members]
}

const openAddForm = () => {
  editingMember.value = null
  formRef.value?.open(null)
}

const openEditForm = (member: TeamMember) => {
  editingMember.value = member
  formRef.value?.open(member)
}

const handleSave = async (formData: FormData) => {
  try {
    if (editingMember.value) {
      await teamMemberStore.updateTeamMember(props.candidateId, editingMember.value.id, formData)
    } else {
      await teamMemberStore.createTeamMember(props.candidateId, formData)
    }
    syncLocalMembers()
    editingMember.value = null
    formRef.value?.closeModal()
  } catch (error: any) {
    alert(error.message || 'Failed to save team member')
  }
}

const handleDelete = async (member: TeamMember) => {
  if (confirm(`Are you sure you want to remove ${member.name} from the team?`)) {
    try {
      await teamMemberStore.deleteTeamMember(props.candidateId, member.id)
      syncLocalMembers()
    } catch (error: any) {
      alert(error.message || 'Failed to remove team member')
    }
  }
}
</script>

<template>
  <div class="space-y-4">
    <div v-if="localMembers.length === 0" class="rounded-lg border border-dashed border-gray-300 p-6 text-center">
      <p class="text-gray-500">No team members added yet.</p>
    </div>

    <ul v-else ref="sortableRef" class="space-y-2">
      <li
        v-for="member in localMembers"
        :key="member.id"
        class="flex items-center gap-3 rounded-lg border bg-white p-3"
      >
        <VButton v-if="!disabled" class="drag-handle cursor-move" size="xs-narrow" variant="invisible">
          <VIcon name="Move" class="h-5 w-5 text-gray-400" />
        </VButton>

        <VAvatar :image="member.photo_url" :initials="member.initials" size="sm" />

        <div class="min-w-0 flex-1">
          <div class="font-medium text-gray-900">{{ member.name }}</div>
          <div v-if="member.title" class="text-sm text-gray-500">{{ member.title }}</div>
        </div>

        <div v-if="!disabled" class="flex items-center gap-1">
          <VButton size="xs-narrow" variant="invisible" :click="() => openEditForm(member)">
            <VIcon
              name="Pencil"
              class="h-4 w-4 !fill-gray-400 !stroke-gray-400 hover:!fill-gray-600 hover:!stroke-gray-600"
            />
          </VButton>
          <VButton size="xs-narrow" variant="invisible" :click="() => handleDelete(member)">
            <VIcon name="Trash" class="h-4 w-4 text-gray-400 hover:text-red-500" />
          </VButton>
        </div>
      </li>
    </ul>

    <VButton v-if="!disabled" variant="secondary" size="sm" :click="openAddForm">
      <span class="flex items-center">
        <VIcon name="Plus" class="mr-1 h-4 w-4" />
        Add Team Member
      </span>
    </VButton>

    <TeamMemberForm
      ref="formRef"
      :member="editingMember"
      :disabled="disabled"
      :candidate-id="candidateId"
      @save="handleSave"
      @cancel="editingMember = null"
    />
  </div>
</template>
